/**
 * PdfProvenanceViewer
 *
 * Renders the original PDF for a Knowledge Object and optionally highlights
 * the page(s) + bounding boxes corresponding to the selected KnowledgeUnit's
 * provenance records.
 *
 * Layout contract:
 *   - The parent must give this component a fixed height (e.g. `height: 100%`
 *     inside a flex column). The component manages its own scroll internally.
 *   - `knowledgeObjectId` → URL of the /pdf proxy endpoint.
 *   - `provenance` → array of {page_no, bbox, page_width, page_height,
 *     coord_origin} from `unit.metadata.provenance`; pass `null` / `undefined`
 *     when no unit is selected (no highlights shown).
 *   - `activePage` → controlled prop so the parent can jump to a page.
 *     Defaults to the first provenance page when `provenance` changes.
 *
 * ─── Coordinate system ───────────────────────────────────────────────────────
 *
 * Docling bbox uses BOTTOM-LEFT origin (PDF convention) by default:
 *   l/t/r/b in points; b < t (y increases upward).
 *
 * Some Docling versions / SaaS results use TOPLEFT origin:
 *   l/t/r/b in points; t < b (y increases downward, CSS-native).
 *
 * We read `coord_origin` from each provenance record and apply the correct
 * Y-axis transform:
 *   BOTTOMLEFT: normalised_y = 1 - (t / page_height)   ← flip
 *   TOPLEFT:    normalised_y = t / page_height           ← no flip
 *
 * react-pdf renders each page in a <canvas>. We overlay an absolutely-
 * positioned <svg> on top and draw highlight <rect>s by converting from
 * Docling coordinates to CSS-pixel fractions.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { InlineLoading } from '@carbon/react';
import { ChevronLeft, ChevronRight } from '@carbon/icons-react';

import type { UnitProvenanceRecord } from '@shared/types/knowledge-base';
import { env } from '@/config/env';
import { authHeader } from '@/lib/auth/token-store';

// ─── react-pdf worker ────────────────────────────────────────────────────────
// Use a CDN-hosted worker that matches the installed pdfjs-dist version.
// This avoids bundling the worker into the main chunk (saves ~400 KB gzip).
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PdfProvenanceViewerProps {
  /** ID of the knowledge object to load the PDF for. */
  knowledgeObjectId: string;
  /**
   * Provenance records from `unit.metadata.provenance`.
   * Pass `null` or `undefined` to clear highlights (no unit selected).
   */
  provenance?: UnitProvenanceRecord[] | null;
  /** Override the page displayed (1-based). Defaults to first provenance page. */
  activePage?: number;
  onPageChange?: (page: number) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Convert a Docling bbox to a CSS-ready normalised rect, honouring coord_origin.
 * Returns fractions in [0, 1].
 */
function normaliseBbox(prov: UnitProvenanceRecord): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  const { bbox, page_width: pw, page_height: ph } = prov;
  const origin = prov.coord_origin ?? 'BOTTOMLEFT';

  const x = bbox.l / pw;
  const w = (bbox.r - bbox.l) / pw;

  let y: number;
  let h: number;

  if (origin === 'TOPLEFT') {
    // y increases downward — CSS-native, no flip needed.
    y = bbox.t / ph;
    h = (bbox.b - bbox.t) / ph;
  } else {
    // BOTTOMLEFT (PDF default): y increases upward — flip to CSS top-left origin.
    y = 1 - bbox.t / ph;
    h = (bbox.t - bbox.b) / ph;
  }

  // Clamp to [0, 1] and ensure non-negative dimensions.
  const cx = Math.max(0, Math.min(1, x));
  const cy = Math.max(0, Math.min(1, y));
  const cw = Math.max(0.005, Math.min(1 - cx, w));  // at least 0.5% wide
  const ch = Math.max(0.005, Math.min(1 - cy, h));  // at least 0.5% tall

  return { x: cx, y: cy, w: cw, h: ch };
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function PdfProvenanceViewer({
  knowledgeObjectId,
  provenance,
  activePage: activePageProp,
  onPageChange,
}: PdfProvenanceViewerProps) {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageWidth, setPageWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const highlightSvgRef = useRef<SVGSVGElement>(null);

  // ── Fetch PDF blob (authenticated) ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let prevUrl: string | null = null;

    const load = async () => {
      setLoadError(null);
      setPdfBlobUrl(null);
      setNumPages(0);
      // Do NOT reset currentPage here — provenance may already have set it.
      // The page will be re-validated in handleDocumentLoaded once numPages is known.

      const url = `${env.apiUrl || ''}/api/knowledge-objects/${knowledgeObjectId}/pdf`;
      try {
        const resp = await fetch(url, { headers: authHeader() });
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          throw new Error(`PDF fetch failed: ${resp.status} ${text}`);
        }
        const blob = await resp.blob();
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(blob);
        prevUrl = blobUrl;
        setPdfBlobUrl(blobUrl);
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load PDF');
      }
    };

    load();

    return () => {
      cancelled = true;
      if (prevUrl) URL.revokeObjectURL(prevUrl);
    };
  }, [knowledgeObjectId]);

  // ── Sync active page when provenance changes ────────────────────────────────
  useEffect(() => {
    if (activePageProp != null) {
      setCurrentPage(activePageProp);
      return;
    }
    if (provenance && provenance.length > 0) {
      setCurrentPage(provenance[0].page_no);
    }
  }, [provenance, activePageProp]);

  // ── Measure container width for responsive PDF rendering ───────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setPageWidth(entry.contentRect.width);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const handleDocumentLoaded = useCallback(
    ({ numPages: n }: { numPages: number }) => {
      setNumPages(n);
      // Re-apply the target page now that we know the page count, so any page
      // set by the provenance-sync effect (which fired before numPages was
      // available) is clamped to a valid range.
      setCurrentPage((prev) => {
        const target =
          activePageProp != null
            ? activePageProp
            : provenance && provenance.length > 0
              ? provenance[0].page_no
              : prev;
        return Math.max(1, Math.min(n, target));
      });
    },
    [activePageProp, provenance]
  );

  const goTo = (page: number) => {
    const clamped = Math.max(1, Math.min(numPages, page));
    setCurrentPage(clamped);
    onPageChange?.(clamped);
  };

  // ── Highlights for the current page ────────────────────────────────────────
  const pageHighlights = (provenance ?? []).filter((p) => p.page_no === currentPage);

  // ── Auto-scroll the first highlight into view after page render ────────────
  useEffect(() => {
    if (pageHighlights.length === 0 || !highlightSvgRef.current || pageWidth <= 0) return;

    // A short delay lets react-pdf finish rendering the page canvas before we
    // try to scroll. requestAnimationFrame alone isn't enough for PDF rendering.
    const tid = setTimeout(() => {
      const svg = highlightSvgRef.current;
      if (!svg) return;
      const first = pageHighlights[0];
      const { y } = normaliseBbox(first);
      const scrollContainer = svg.closest('.pdf-viewer__scroll') as HTMLElement | null;
      if (!scrollContainer) return;
      const pageEl = svg.closest('.pdf-viewer__page-wrap') as HTMLElement | null;
      if (!pageEl) return;
      // pageEl.offsetTop = page top relative to the scroll container's scroll origin.
      // pageEl.offsetHeight * y = pixel offset of the highlight within the page.
      // Subtract 30% of the visible height to keep the highlight near the top.
      const highlightAbsTop = pageEl.offsetTop + pageEl.offsetHeight * y;
      const targetY = highlightAbsTop - scrollContainer.clientHeight * 0.3;
      scrollContainer.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    }, 150);

    return () => clearTimeout(tid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageHighlights.length, pageWidth]);

  // Whether a unit is selected but has no resolved bbox for this page
  const hasProvenanceButNoHighlight =
    provenance !== null &&
    provenance !== undefined &&
    provenance.length === 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="pdf-viewer" ref={containerRef}>
      {/* Toolbar */}
      <div className="pdf-viewer__toolbar">
        <button
          className="pdf-viewer__nav-btn"
          disabled={currentPage <= 1}
          onClick={() => goTo(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="pdf-viewer__page-label">
          {numPages > 0 ? `${currentPage} / ${numPages}` : '—'}
        </span>
        <button
          className="pdf-viewer__nav-btn"
          disabled={currentPage >= numPages}
          onClick={() => goTo(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>

        {/* Empty-state hint: unit selected but no bbox found */}
        {hasProvenanceButNoHighlight && (
          <span className="pdf-viewer__no-location" title="No precise PDF location found for this unit">
            No location
          </span>
        )}
      </div>

      {/* PDF area */}
      <div className="pdf-viewer__scroll">
        {!pdfBlobUrl && !loadError && (
          <div className="pdf-viewer__loading">
            <InlineLoading description="Loading PDF…" />
          </div>
        )}

        {loadError && (
          <div className="pdf-viewer__error">
            <p>Could not load PDF.</p>
            <p className="pdf-viewer__error-detail">{loadError}</p>
          </div>
        )}

        {pdfBlobUrl && (
          <Document
            file={pdfBlobUrl}
            onLoadSuccess={handleDocumentLoaded}
            loading={<InlineLoading description="Rendering…" />}
            error={<div className="pdf-viewer__error">Failed to parse PDF.</div>}
          >
            <div className="pdf-viewer__page-wrap">
              <Page
                pageNumber={currentPage}
                width={pageWidth > 0 ? pageWidth : undefined}
                renderAnnotationLayer
                renderTextLayer
              />

              {/* Highlight overlay */}
              {pageHighlights.length > 0 && pageWidth > 0 && (
                <svg
                  ref={highlightSvgRef}
                  className="pdf-viewer__highlight-svg"
                  viewBox="0 0 1 1"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  {pageHighlights.map((prov, i) => {
                    const { x, y, w, h } = normaliseBbox(prov);
                    return (
                      <rect
                        key={i}
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        className="pdf-viewer__highlight-rect"
                      />
                    );
                  })}
                </svg>
              )}
            </div>
          </Document>
        )}
      </div>

      {/* Page-jump buttons for provenance pages */}
      {provenance && provenance.length > 1 && (
        <div className="pdf-viewer__prov-pages">
          {[...new Set(provenance.map((p) => p.page_no))].map((pg) => (
            <button
              key={pg}
              className={`pdf-viewer__prov-chip${pg === currentPage ? ' pdf-viewer__prov-chip--active' : ''}`}
              onClick={() => goTo(pg)}
            >
              p.{pg}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
